import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { metricsKeys } from "../query-keys";
import type { MetricsKind } from "@/domain/metrics-params";
import type { BuildId } from "@/types/identifiers";

export { metricsKeys } from "../query-keys";

export function useMetricsSnapshots(buildId: BuildId) {
  return useQuery({
    queryKey: metricsKeys.byBuild(buildId),
    queryFn: async () => {
      const result = await api.GET("/api/builds/{build_id}/metrics", {
        params: { path: { build_id: buildId } },
      });
      return throwIfError(result, "Failed to fetch metrics recordings");
    },
    enabled: !!buildId,
  });
}

export function useMetricsHistory(buildId: BuildId, kind: MetricsKind) {
  return useQuery({
    queryKey: metricsKeys.history(buildId, kind),
    queryFn: async () => {
      const result = await api.GET("/api/builds/{build_id}/metrics/{kind}/history", {
        params: { path: { build_id: buildId, kind } },
      });
      return throwIfError(result, "Failed to fetch metrics history");
    },
    enabled: !!buildId,
  });
}

export function useFetchMetrics(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (kind: MetricsKind) => {
      const result = await api.POST("/api/builds/{build_id}/metrics/{kind}/fetch", {
        params: { path: { build_id: buildId, kind } },
      });
      return throwIfError(result, "Failed to record metrics from NVP");
    },
    onSuccess: (_data, kind) => {
      // Scoped to what changed: this build's count, this boat+kind's history,
      // and the fleet view. The WS broadcast covers other clients coarsely.
      qc.invalidateQueries({ queryKey: metricsKeys.byBuild(buildId) });
      qc.invalidateQueries({ queryKey: metricsKeys.history(buildId, kind) });
      qc.invalidateQueries({ queryKey: metricsKeys.fleet });
    },
  });
}

export function useStartRecording(buildId: BuildId) {
  return useMutation({
    mutationFn: async ({ kind, durationS }: { kind: MetricsKind; durationS: number }) => {
      const result = await api.POST("/api/builds/{build_id}/metrics/{kind}/start", {
        params: { path: { build_id: buildId, kind } },
        body: { duration_s: durationS },
      });
      return throwIfError(result, "Failed to start the recording");
    },
  });
}

export function useFleetMetrics() {
  return useQuery({
    queryKey: metricsKeys.fleet,
    queryFn: async () => {
      const result = await api.GET("/api/fleet-metrics");
      return throwIfError(result, "Failed to fetch fleet metrics");
    },
  });
}
